import { inject, computed, provide, ref, watch, onMounted, type Ref } from 'vue'
import { useMachine } from '@xstate/vue'
import { fromPromise } from 'xstate'
import get from 'lodash/get'
import type { ActorRefFrom } from 'xstate'
import { client } from '~/client'
import { formSettings, type FormSettings } from '~/form-settings'
import type { ClientRequest } from '~/types'
import { formBase, form, applyFormRefines, machine } from './form.machine'
import type { ErrorBag, Form } from './form-types'
import { card, dafAmount, status } from './status'
import { queryParams } from '~/shared/query-params'
import { resolvePaymentMethodLabel } from '~/shared/payment-method'
import { executeRecaptcha, getRecaptchaConfig, loadRecaptcha } from '~/shared/recaptcha'
import { removeSensePassWidgetQueryParam } from '~/shared/runtime-environment'

type Card = { number: string, cvc: string, month: string, year: string }

function isAFormId (id?: string | number): id is keyof typeof formSettings {
  if (!id) return false

  return Object.keys(formSettings).includes("" + id)
}

function getElementPaymentMethod(element: any) {
  if (!element || typeof element.getPaymentMethod !== 'function') return null

  try {
    const paymentMethod = element.getPaymentMethod()

    return typeof paymentMethod === 'string' ? paymentMethod : null
  } catch (e) {
    return null
  }
}

export function usePaymentForm (formId?: string, options?: { testMode?: boolean, noCharge?: boolean, onSuccess?: () => void }) {
  if (!formId) {
    return inject<PaymentForm>('paymentForm')!
  }

  const settings = isAFormId(formId) && formSettings[formId] ? formSettings[formId] : undefined

  const formSchema = settings!.customValidations ? applyFormRefines(settings!.customValidations(formBase)) : form

  onMounted(() => {
    const recaptchaConfig = !options?.noCharge ? getRecaptchaConfig(settings) : null
    if (!recaptchaConfig) return

    loadRecaptcha(recaptchaConfig.siteKey).catch(error => {
      console.error('reCAPTCHA could not be loaded', error)
    })
  })

  const request = ref<ClientRequest>()
  const ccElement = ref<any>(null)

  // Function to process applications array with percentages
  function processApplications(settings: FormSettings, amount: number) {
    const applications: Array<{
      amount: number
      department: string
      campaign: string
      subCampaign?: string
    }> = []

    // If the new applications array is defined, use it
    if (settings.applications && settings.applications.length > 0) {
      settings.applications.forEach(app => {
        applications.push({
          amount: Math.round((amount * app.percentage / 100) * 100) / 100,
          department: app.department,
          campaign: app.campaign,
          subCampaign: app.subCampaign
        })
      })
    } else {
      // Fallback to legacy single department/campaign structure
      if (settings.department && settings.campaign) {
        applications.push({
          amount: amount,
          department: settings.department,
          campaign: settings.campaign,
          subCampaign: settings.subCampaign
        })
      }
    }

    return applications
  }

  const machineWithImplementations = machine.provide({
    actions: {
      resetCard(ctx: any) {
        const element = ccElement.value?.$el || ccElement.value;
        if (element && typeof element.resetCard === 'function') {
          element.resetCard()
        }
      },
      notifySucceeded() {
        options?.onSuccess?.()
      }
    },
    actors: {
      submit: fromPromise(async ({ input }: any) => {
        const ctx = input
        if (!ctx.validForm) {
          throw new Error('Somehow we got here without a valid form')
        }

        if (status.value?.id != 'valid' && !['check','wire'].includes(ctx.validForm.gateway)) {
          throw new Error('CARD_NOT_VALID')
        }

        const recaptchaConfig = !options?.noCharge ? getRecaptchaConfig(settings) : null
        let recaptchaToken: string | null = null
        if (recaptchaConfig) {
          try {
            recaptchaToken = await executeRecaptcha(recaptchaConfig)
          } catch (error) {
            console.error('reCAPTCHA could not be completed', error)
            throw new Error('CAPTCHA_NOT_AVAILABLE')
          }
        }

        let token: any
        let selectedPaymentMethod: string | null = null
        
        const handleProcessing = ctx.validForm.gateway == 'check' || ctx.validForm.gateway == 'wire' ? false : !!ctx.validForm.handleProcessing

        let amount = handleProcessing ? Math.round((ctx.validForm.amount + ctx.validForm.amount * ctx.validForm.transactionFee / 100) * 100) / 100 : ctx.validForm.amount

        try {
          if (ctx.validForm.gateway.toLowerCase().startsWith('cardknox') || ['check','wire'].includes(ctx.validForm.gateway)) {
            token = card.value
          } else {
            // For SensePass/Cardcom, use element-based API
            // Poll for getToken method availability (it's added during the widget's mounted lifecycle)
            let element = ccElement.value?.$el || ccElement.value;
            let retries = 0;
            const maxRetries = 20; // 1 second total (20 * 50ms)

            while (retries < maxRetries && (!element || typeof element.getToken !== 'function')) {
              console.log(`[Submit] Waiting for getToken method... attempt ${retries + 1}/${maxRetries}`);
              await new Promise(resolve => setTimeout(resolve, 50));
              element = ccElement.value?.$el || ccElement.value;
              retries++;
            }

            if (element && typeof element.getToken === 'function') {
              console.log('[Submit] ✅ Using element.getToken()');
              token = await element.getToken(amount, ctx.validForm.currency)
              selectedPaymentMethod = getElementPaymentMethod(element)
            } else {
              console.error('[Submit] ❌ Credit card element or getToken method not available after waiting');
              throw new Error('Credit card API not available')
            }
          }
        }
        catch (error: any) {
          throw new Error(`Tokenization ${error.message}`)
        }

        if (!token && !ctx.validForm.gateway.toLowerCase().startsWith('cardcom') && !['check','wire'].includes(ctx.validForm.gateway)) {
          throw new Error(`This shouldn't happen`)
        }

        // handle case where dafPay donor changes the amount
        if (dafAmount.value) {
          amount = handleProcessing ? Math.round((dafAmount.value + dafAmount.value * ctx.validForm.transactionFee / 100) * 100) / 100 : dafAmount.value
        }

        // Only process applications if settings contains multiple applications
        if (settings && Array.isArray(settings.applications) && settings.applications.length > 1) {
          const processedApplications = processApplications(settings, amount)
          if (processedApplications.length > 0) {
            ctx.validForm.applications = processedApplications
          } else {
            ctx.validForm.applications[0].amount = amount
          }
        } else {
          // Fallback for cases without multiple applications in settings
          if (ctx.validForm.applications && ctx.validForm.applications.length > 1) {
            // Applications already built dynamically by Vue component - leave them alone
          } else {
            // Single application case - set the amount
            ctx.validForm.applications[0].amount = amount
          }
        }

        const description = ctx.validForm.applications[0].campaign == 'ADM Emergency JSU'
         ? 'MDSNC2401T'
         : ctx.validForm.description

        // Convert limited monthly (dayOfMonth + periodMonths) to split payment
        // to avoid SensePassCardknox bug where applications amount is divided by 100
        // This makes "$50/month for 12 months" look exactly like "$600 split into 12 payments"
        const isLimitedMonthly = ctx.validForm.dayOfMonth && ctx.validForm.periodMonths
        const periodMultiplier = isLimitedMonthly && ctx.validForm.periodMonths ? parseInt(ctx.validForm.periodMonths) : 1

        const totalAmount = isLimitedMonthly ? amount * periodMultiplier : amount
        const applications = isLimitedMonthly
          ? ctx.validForm.applications.map(app => ({
              ...app,
              amount: (parseFloat(String(app.amount || 0))) * periodMultiplier
            }))
          : ctx.validForm.applications

        request.value = {
          signup: ctx.validForm.signup,
          callback: ctx.validForm.callback,
          success: ctx.validForm.success,
          dayOfMonth: isLimitedMonthly ? undefined : (ctx.validForm.dayOfMonth || undefined),
          recurrenceStartDate: isLimitedMonthly ? undefined : (ctx.validForm.dayOfMonth ? new Date().toISOString().slice(0, 8) + ctx.validForm.dayOfMonth : undefined),
          dayOfWeek: ctx.validForm.dayOfWeek || undefined,
          periodMonths: ctx.validForm.periodMonths || undefined,
          periodWeeks: ctx.validForm.periodWeeks || undefined,
          form: formId,
          referer_url: removeSensePassWidgetQueryParam(location.href),
          currency: ctx.validForm.currency,
          gateway: ctx.validForm.gateway,
          title: ctx.validForm.title,
          email: ctx.validForm.email?.trim(),
          description,
          firstName: ctx.validForm.firstName,
          lastName: ctx.validForm.lastName,
          taxId: ctx.validForm.taxId,
          address: ctx.validForm.city ? {
            city: ctx.validForm.city,
            state: ctx.validForm.state!,
            country: ctx.validForm.country!,
            street: ctx.validForm.street!,
            street2: ctx.validForm.street2,
            zip: ctx.validForm.zip!,
            phone: ctx.validForm.phone!
          } : undefined,
          achToken: token?.achToken,
          routingNumber: token?.routingNumber,
          expirationMonth: token?.expMonth,
          expirationYear: token?.expYear,
          CardNumber: token?.card,
          SecurityCode: token?.cvv,
          paymentMethod: resolvePaymentMethodLabel({
            paymentMethod: selectedPaymentMethod,
            gateway: ctx.validForm.gateway,
            achToken: token?.achToken,
            CardNumber: token?.card || token?.number,
            card: token
          }),
          amount: totalAmount,
          transactionFee: handleProcessing ? ctx.validForm.transactionFee : 0,
          token: token?.token,
          applications,
          AccountID: ctx.validForm.accountID,
          honoreeName: ctx.validForm.honoreeName,
          dedication: ctx.validForm.dedication,
          dedicationMessage: ctx.validForm.dedicationMessage,
          recipientEmail: ctx.validForm.recipientEmail,
          recipientName: ctx.form.recipientName,
          custom: ctx.validForm.custom,
          pledgeId: ctx.validForm.pledgeId,
          channel: queryParams().channel,
          recaptchaToken
        }

        if (!options?.noCharge) {
          const result = await client({
            method: 'chargeCard',
            params: request.value,
          })

          return result
        }

        return {
          success: true
        }

      })
    }
  })

  const machineReturn = useMachine(machineWithImplementations, {
    input: {
      validationSchema: formSchema,
      testMode: options?.testMode,
      form: {
        applications: settings ? processApplications(settings, 0) : [{ amount: null, department: null, campaign: null, subCampaign: null }],
        currency: settings?.defaultCurrency ?? 'USD',
        transactionFee: 2.5,
        dayOfMonth: null,
        dayOfWeek: null,
        periodMonths: null,
        periodWeeks: null,
        signup: false,
        title: '',
        firstName: null,
        lastName: null,
        accountID: null,
        taxId: null,
        street: null,
        street2: null,
        city: null,
        country: 'US',
        state: null,
        zip: null,
        amount: null,
        handleProcessing: false,
        email: null,
        phone: null,
        card: null,
        recipientName: null,
        recipientEmail: null,
        honoreeName: null,
        dedication: null,
        dedicationMessage: null,
        receipt: null,
        description: null,
        toc: false,
        gateway: null,
        custom: {},
        pledgeId: null
      },
      redirect: !!settings?.message,
      noCharge: options?.noCharge || false
    }
  }) as any

  const send = machineReturn.send
  const snapshot = machineReturn.snapshot
  const actorRef = machineReturn.actorRef

  console.log('Initial snapshot context:', snapshot.value.context)
  console.log('Applications:', snapshot.value.context.form.applications)
  console.log('First application:', snapshot.value.context.form.applications[0])
  console.log('Settings:', settings)

  // Store actorRef globally for window event handlers
  if (typeof window !== 'undefined') {
    ;(window as any).formService = actorRef
  }

  function isCustom (field: string): field is `custom.${string}` {
    return field.slice(0, 6) == 'custom'
  }

  function setTouched (field: keyof typeof snapshot.value.context.form) {
    send({ type: 'SET_TOUCHED', field })
  }

  function submit () {
    send({ type: 'SUBMIT' })
  }

  function getValue<T extends keyof Form | `custom.${string}` | `application.${string}.${string}`> (field: T) {
    if (field.includes('applications')) {
      const [, index, f] = field.split('.')
      //@ts-ignore
      // apologies to the TS gods
      return snapshot.value.context.form.applications[index]?.[f]
    }

    if (isCustom(field)) {
      //@ts-ignore
      return snapshot.value.context.form.custom?.[field.replace('custom.', '')]
    }

    //@ts-ignore
    return snapshot.value.context.form[field]
  }

  function setValue<T extends keyof Form | `custom.${string}` | `application.${number}.${'department' | 'campaign' | 'subCampaign' | 'amount'}` | 'department' | 'campaign' | 'subCampaign'> (field: T, value: T extends keyof Form ? Form[T] : string) {
    if (field == 'department' || field == 'campaign' || field == 'subCampaign' || field == 'amount') {
      if (field == 'amount') {
        //@ts-ignore
        send({ type: 'SET_VALUE', field, value })
      }
      //@ts-ignore
      return send({ type: 'SET_APPLICATION', index: 0, field, value: value })
    }
    if (field.includes('applications')) {
      const [, index, f] = field.split('.')
      //@ts-ignore
      // apologies to the TS gods
      return send({ type: 'SET_APPLICATION', index, field: f, value: value })
    }

    if (isCustom(field)) {
      // @ts-ignore
      // just can't get TS happy here
      return send({ type: 'SET_CUSTOM', field, value })
    }

    // @ts-ignore
    // just can't get TS happy here
    send({ type: 'SET_VALUE', field, value })
  }

  // Create reactive refs for state matching (same pattern as payment-button.vue)
  const isSubmitting = ref(snapshot.value.matches('submitting'))
  const isSubmitted = ref(snapshot.value.matches('submitted'))

  // Subscribe to state changes to update reactive refs
  actorRef.subscribe((state) => {
    isSubmitting.value = state.matches('submitting')
    isSubmitted.value = state.matches('submitted')
  })

  const paymentForm: PaymentForm = {
    setValue: setValue as any, // If I didn't do this, got an infinite recursion error
    getValue: getValue as any, // ""
    form: computed(() => snapshot.value.context.form),
    gateways: computed(() => {
      const application = snapshot.value.context.form.applications[0]

      if (!application || !snapshot.value.context.gatewayMapping) return null

      return snapshot.value.context.gatewayMapping[application.department!]
    }),
    isTouched: (field: keyof Form) => get(snapshot.value.context.touched, field.replace(/\./g, ',')),
    setTouched,
    errors: computed(() => snapshot.value.context.validations),
    submit,
    settings,
    submitting: computed(() => isSubmitting.value),
    submitted: computed(() => isSubmitted.value),
    actorRef,
    request,
    testMode: options?.testMode!,
    ccElement,
    setCcElement: (element: any) => {
      ccElement.value = element
    }
  }

  provide('paymentForm', paymentForm)

  watch(status, () => {
    if (status.value?.id  == 'valid' && status.value?.amount) {
      dafAmount.value = status.value.amount
    }
  })

  return paymentForm
}

type SetValue = <T extends keyof Form | `custom.${string}` | `applications.${number}.${'department' | 'campaign' | 'subCampaign' | 'amount'}` | 'department' | 'campaign' | 'subCampaign'> (field: T, value: T extends keyof Form ? Form[typeof field] : string | null) => void
type GetValue = <T extends keyof Form | `custom.${string}`> (field: T) => T extends keyof Form ? Form[typeof field] : string | null

export type PaymentForm = {
  setValue: SetValue,
  getValue: GetValue,
  form: Ref<Form>,
  gateways: any,
  testMode: boolean,
  isTouched: (field: keyof Form) => boolean,
  setTouched: (field: keyof Form) => void,
  errors: Ref<Partial<ErrorBag>>,
  submit: () => void,
  settings?: FormSettings,
  submitted: Ref<boolean>,
  submitting: Ref<boolean>,
  actorRef: ActorRefFrom<typeof machine>,
  request: any,
  ccElement: Ref<any>,
  setCcElement: (element: any) => void
}
